import * as stylex from "@stylexjs/stylex";
import { Annotation, HtmlLabel } from "@visx/annotation";
import { Group } from "@visx/group";
import { springConfigs } from "charts/lib/motion";
import { motion } from "framer-motion";
import { useRTL } from "hooks";
import { getContrastText } from "lib/colorManipulator";
import React from "react";
import * as M from "../../../../materials";
import { typography } from "../../../../materials/typography.stylex";
const ANNOTATION_ARROW_DX = 24;
// "230px"/"24px" below mirror ANNOTATION_WIDTH/ANNOTATION_ARROW_DX in ../constants.tsx —
// stylex.create() needs literal values, it can't resolve a plain module import.
const styles = {
    annotation: {
        kaIpWk: "x12oqio5",
        kmVPX3: "x153ncpu",
        kzqmXN: "x3smdqs",
        kwXMNM: "x1j6awrg",
        k3foIR: "x1srwf7z",
        kH8cDV: "xyqeioi",
        k5JduY: "xs5nyxy",
        k4zj60: "x1gmr53x",
        kkgrvl: "xkj6t0a",
        kloYau: "x2q1x1w",
        kPGmMm: "x19g77sy",
        kbwMcY: "x86avzo",
        kc1AQg: "x1yxci0c",
        kvRJ0G: "xhhgppv",
        kcGwG1: "x1oykil0",
        kfzc6R: "x38x5k8",
        k55e2J: "x1ru3uqb",
        $$css: true
    },
    ltr: {
        ksoyAI: "xzt5al7",
        $$css: true
    },
    rtl: {
        ksoyAI: "xzyj77d",
        $$css: true
    }
};
const RuleAnnotation = React.memo(({ style, text, x, y })=>{
    const isRTL = useRTL();
    return <Annotation dx={-ANNOTATION_ARROW_DX} dy={-24} x={x} y={y}>
      <HtmlLabel verticalAnchor="end" horizontalAnchor="start" showAnchorLine={false} containerStyle={{
        position: "fixed"
    }}>
        <motion.div {...stylex.props(typography.meta, styles.annotation, isRTL ? styles.rtl : styles.ltr)} style={{
        color: getContrastText(style.stroke, {
            light: M.whiteText,
            dark: M.blackText
        }),
        backgroundColor: style.stroke,
        "--rule-stroke": style.stroke
    }} initial={{
        opacity: 0
    }} animate={{
        opacity: 1
    }} exit={{
        opacity: 0
    }} transition={springConfigs.gentle}>
          <p>{text}</p>
        </motion.div>
      </HtmlLabel>
    </Annotation>;
});
function Rule({ rule, scaleX, scaleY, activePointX }) {
    const { start, end, style } = rule;
    const shouldRenderAnnotation = (point)=>{
        const conditions = [
            point.annotation,
            activePointX === point.datum.x || point?.annotation?.isAlwaysVisible
        ];
        return conditions.every(Boolean);
    };
    const coordinates = {
        x1: scaleX(start.datum.x) ?? 0,
        y1: scaleY(start.datum.y) ?? 0,
        x2: scaleX(end.datum.x) ?? 0,
        y2: scaleY(end.datum.y) ?? 0
    };
    if (coordinates.x1 <= scaleX.range()[0] || coordinates.x2 > scaleX.range()[1]) return <g/>;
    return <Group>
      <motion.line initial={false} animate={{
        opacity: 1,
        ...coordinates
    }} transition={{
        duration: 0.05,
        ease: "linear"
    }} {...style}/>

      {[
        start,
        end
    ].map((point, index)=><Group key={index}>
          {point.symbol && <circle cx={scaleX(point.datum.x)} cy={scaleY(point.datum.y)} r={3} fill={style.stroke}/>}
          {shouldRenderAnnotation(point) && <RuleAnnotation x={scaleX(point.datum.x) ?? 0} y={scaleY(point.datum.y) ?? 0} text={point?.annotation?.text} style={style}/>}
        </Group>)}
    </Group>;
}
export default React.memo(Rule);
